import type {
  CourseRegistrationManagementCourseAccessViewModel,
  CourseRegistrationManagementFinancialDiscountViewModel,
  CourseRegistrationManagementHistoryItemViewModel,
  CourseRegistrationManagementPoloHistoryItemViewModel,
  CourseRegistrationManagementScholarshipItemViewModel,
  CourseRegistrationManagementSelectiveProcessViewModel,
} from '@/shared/api/generated';

// ─── Enrollment Status (16 statuses matching legacy) ─────────────────────────

export type EnrollmentStatus =
  | 'payment_selection'
  | 'payment_pending'
  | 'approval_pending'
  | 'approved'
  | 'activating'
  | 'active'
  | 'transferred'
  | 'transferred_in_registration'
  | 'canceled'
  | 'canceled_in_registration'
  | 'rejected'
  | 'finished_with_pendencies'
  | 'finished'
  | 'finished_with_monograph'
  | 'finished_with_certificate'
  | 'blocked';

export type EnrollmentStatusGroup = 'in_progress' | 'finished' | 'denied' | 'transferred';

export const ENROLLMENT_STATUS_GROUPS: Record<EnrollmentStatusGroup, EnrollmentStatus[]> = {
  in_progress: [
    'payment_selection',
    'payment_pending',
    'approval_pending',
    'approved',
    'activating',
    'active',
  ],
  finished: [
    'finished',
    'finished_with_certificate',
    'finished_with_monograph',
    'finished_with_pendencies',
  ],
  denied: ['rejected', 'canceled', 'canceled_in_registration', 'blocked'],
  transferred: ['transferred', 'transferred_in_registration'],
};

// ─── Documentation Status ────────────────────────────────────────────────────

export type DocumentationStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'approved_temporary'
  | 'not_sent';

// ─── Scholarship (within enrollment context) ─────────────────────────────────

export type EnrollmentScholarshipStatus = 'none' | 'partial' | 'full';

/**
 * Situação da bolsa no fluxo de concessão, derivada do enum `EScholarshipStatus`
 * da API (1–6). Diferente de {@link EnrollmentScholarshipStatus}, que representa a
 * abrangência do desconto (sem bolsa / parcial / integral).
 */
export type ScholarshipEnrollmentStatus =
  | 'interested'
  | 'in_process'
  | 'scholarship_holder'
  | 'refused'
  | 'refused_interested'
  | 'refused_potential';

// ─── Document Types ──────────────────────────────────────────────────────────

export interface EnrollmentDocument {
  readonly id: number;
  /** ID do anexo (arquivo) usado para atualizar status; `null` quando não enviado. */
  readonly attachmentId: number | null;
  readonly type: string;
  readonly fileName: string;
  readonly fileUrl: string;
  readonly status: DocumentationStatus;
  readonly uploadedAt: string;
  readonly fileSize?: string | null;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
  readonly rejectionReason: string | null;
}

// ─── Enrollment History ──────────────────────────────────────────────────────

/**
 * Histórico de status de uma matrícula.
 * Usa os nomes de campo gerados pelo Kubb com `oldStatus`/`newStatus` mapeados para string.
 */
export type EnrollmentHistory = Omit<
  CourseRegistrationManagementHistoryItemViewModel,
  'oldStatus' | 'newStatus'
> & {
  readonly oldStatus: EnrollmentStatus | null;
  readonly newStatus: EnrollmentStatus;
};

// ─── Polo Change History ─────────────────────────────────────────────────────

/**
 * Histórico de mudança de polo de uma matrícula.
 * Usa os nomes de campo gerados pelo Kubb: `oldPoloName`, `newPoloName`, `dateLog`, `executedBy`.
 */
export type PoloChangeHistory = CourseRegistrationManagementPoloHistoryItemViewModel;

// ─── Course Access ───────────────────────────────────────────────────────────

/**
 * Configuração de acesso ao curso de uma matrícula.
 * Alias direto do tipo gerado pelo Kubb.
 */
export type CourseAccess = CourseRegistrationManagementCourseAccessViewModel;

// ─── Scholarship Detail ──────────────────────────────────────────────────────

/**
 * Bolsa associada a uma matrícula.
 * Usa os nomes de campo gerados pelo Kubb com `status` mapeado para string.
 */
export type ScholarshipDetail = Omit<
  CourseRegistrationManagementScholarshipItemViewModel,
  'scholarshipStatus'
> & {
  readonly status: EnrollmentScholarshipStatus;
  /** Situação no fluxo de concessão (enum `EScholarshipStatus`); `null` se não informado. */
  readonly enrollmentStatus: ScholarshipEnrollmentStatus | null;
};

// ─── Scholarship Full (management detail view) ────────────────────────────────

export type ScholarshipActiveStatus = 'active' | 'suspended' | 'cancelled' | 'pending_docs';

export interface ScholarshipDocumentItem {
  readonly id: number;
  readonly type: string;
  readonly fileName: string | null;
  readonly fileUrl: string | null;
  readonly status: DocumentationStatus;
  readonly uploadedAt: string | null;
  readonly fileSize: string | null;
  readonly approvedBy: string | null;
  readonly approvedAt: string | null;
  readonly rejectionReason: string | null;
}

export interface ScholarshipHistoryItem {
  readonly id: number;
  readonly oldStatus: ScholarshipActiveStatus | null;
  readonly newStatus: ScholarshipActiveStatus;
  readonly changedAt: string;
  readonly changedBy: string;
  readonly changedByRole: string;
  readonly action: string | null;
  readonly justification: string | null;
  readonly observation: string | null;
  readonly planDescription: string | null;
}

export interface EnrollmentScholarshipFull {
  readonly planId: number;
  readonly planDescription: string;
  readonly scholarshipType: string;
  readonly activeStatus: ScholarshipActiveStatus;
  readonly schoolDiscount: number;
  readonly monthlyValue: number;
  readonly validFrom: string;
  readonly validUntil: string | null;
  readonly autoRenew: boolean;
  readonly autoRenewDescription: string | null;
  readonly docDeadline: string | null;
  readonly docDeadlineNote: string | null;
  readonly extensionsGranted: number;
  readonly documents: readonly ScholarshipDocumentItem[];
  readonly reanalysisNotice: { readonly title: string; readonly message: string } | null;
  readonly history: readonly ScholarshipHistoryItem[];
}

// ─── Selective Process ───────────────────────────────────────────────────────

export type AdmissionType = 'vestibular' | 'interview' | 'transfer' | 'other';
export type SelectiveProcessStatus =
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'waiting_exam'
  | 'waiting_interview';

/**
 * Processo seletivo de uma matrícula.
 * Usa os nomes de campo gerados pelo Kubb com status e admissionType mapeados para string.
 * `isAttendEntranceExam` substitui `attendedEntranceExam` (nome Kubb).
 */
export type SelectiveProcess = Omit<
  CourseRegistrationManagementSelectiveProcessViewModel,
  'status' | 'admissionType' | 'isAttendEntranceExam'
> & {
  readonly status: SelectiveProcessStatus;
  readonly admissionType: AdmissionType;
  readonly isAttendEntranceExam: boolean;
  readonly interviewerName?: string | null;
  readonly documentationRejectReason: string | null;
};

// ─── AIA / AIR (Aluno Indica Aluno) ─────────────────────────────────────────

export type AIAProgramAccessStatus = 'active' | 'pending_activation' | 'blocked';

export interface AIADetails {
  readonly indicatedByName: string;
  readonly indicatedByRole: string;
  readonly indicatedByClass: string;
  readonly fromForm: boolean;
  readonly fromFormTechnicalNote: string | null;
  readonly hash: string;
  readonly hashGeneratedAt: string;
  readonly programAccessStatus: AIAProgramAccessStatus;
  readonly programAccessNote: string | null;
}

// ─── Audit Log (action-level trackable) ──────────────────────────────────────

export type AuditLogArea =
  | 'status'
  | 'scholarship'
  | 'financial'
  | 'polo'
  | 'documentation'
  | 'enrollment';

export interface EnrollmentAuditLogEntry {
  readonly id: number;
  readonly changedAt: string;
  readonly changedBy: string;
  readonly changedByRole: string | null;
  readonly area: AuditLogArea;
  readonly action: string;
  readonly detail: string | null;
}

export interface EnrollmentCreationMeta {
  readonly createdAt: string;
  readonly createdBy: string;
  readonly createdVia: string | null;
  readonly createdOrigin: string | null;
  readonly createdIp: string | null;
}

export interface EnrollmentLastChangeMeta {
  readonly changedAt: string;
  readonly changedBy: string;
  readonly changedByRole: string | null;
  readonly changedArea: string | null;
  readonly version: number | null;
}

// ─── Audit Trail ─────────────────────────────────────────────────────────────

export interface AuditEntry {
  readonly id: number;
  readonly entityName: string;
  readonly fieldName: string;
  readonly oldValue: string | null;
  readonly newValue: string | null;
  readonly action: 'create' | 'update' | 'delete';
  readonly changedAt: string;
  readonly changedBy: string;
  readonly ipAddress: string | null;
}

// ─── Enrollment (expanded) ───────────────────────────────────────────────────

export interface Enrollment {
  readonly id: number;
  readonly personId: number;
  readonly personName: string;
  readonly personEmail: string;
  readonly personCpf: string;
  readonly courseName: string;
  readonly courseId: number;
  readonly className: string;
  readonly classId: number;
  readonly poleName: string | null;
  readonly poleId: number | null;
  readonly status: EnrollmentStatus;
  readonly documentationStatus: DocumentationStatus;
  readonly scholarshipStatus: EnrollmentScholarshipStatus;
  readonly enrollmentDate: string;
  readonly startDate: string;
  readonly endDate: string;
  readonly grade: number | null;
  readonly attendance: number | null;
  readonly progressPercentage: number | null;
  readonly isForeign: boolean;
  readonly foreignStatus: string | null;
  readonly isPcd: boolean;
  readonly observation: string | null;
  readonly statusReason: string | null;
  readonly createdAt: string;
  // Person detail fields (optional, included in management detail view)
  readonly personAlias?: string | null;
  readonly personRg?: string | null;
  readonly personPassaporte?: string | null;
  readonly personBirthDate?: string | null;
  readonly personLanguage?: string | null;
  readonly personPhoneMobile?: string | null;
  readonly personPhoneWork?: string | null;
  readonly personPhoneHome?: string | null;
  readonly personPhonePreference?: string | null;
  readonly personAddress?: string | null;
  // Enrollment extra flags
  readonly vendorName?: string | null;
  readonly isIndicatedByStudent?: boolean;
  readonly hasForeignDiploma?: boolean;
  readonly isScholarshipByLink?: boolean;
  // Course extra display fields
  readonly courseOrganization?: string | null;
  readonly studentsCount?: number | null;
  readonly courseInstructions?: string | null;
  // Relations
  readonly documents: readonly EnrollmentDocument[];
  readonly history: readonly EnrollmentHistory[];
  readonly poloChanges: readonly PoloChangeHistory[];
  readonly courseAccess: CourseAccess;
  readonly scholarships: readonly ScholarshipDetail[];
  readonly selectiveProcess: SelectiveProcess | null;
  readonly auditTrail: readonly AuditEntry[];
  readonly financialSummary?: EnrollmentFinancialSummary | null;
  readonly scholarshipFull?: EnrollmentScholarshipFull | null;
  readonly aiaDetails?: AIADetails | null;
  readonly creationMeta?: EnrollmentCreationMeta | null;
  readonly lastChangeMeta?: EnrollmentLastChangeMeta | null;
  readonly auditLog?: readonly EnrollmentAuditLogEntry[] | null;
}

// ─── Grouped View (by Course/Class) ─────────────────────────────────────────

export interface ClassStudentGroup {
  readonly courseId: number;
  readonly courseName: string;
  readonly classId: number;
  readonly className: string;
  readonly poleName: string | null;
  readonly studentsCount: number;
  readonly enrollments: readonly Enrollment[];
}

// ─── Filters ─────────────────────────────────────────────────────────────────

export interface EnrollmentFilters {
  readonly company: string;
  readonly polo: string;
  readonly email: string;
  readonly cpf: string;
  readonly studentName: string;
  readonly statuses: readonly EnrollmentStatus[];
  readonly courseId: string;
  readonly documentationFilter: 'all' | 'with_documents' | 'without_documents';
  readonly documentStartDate: string;
  readonly documentEndDate: string;
}

// ─── Financial Summary ───────────────────────────────────────────────────────

/**
 * Desconto financeiro da matrícula.
 * Alias direto do tipo gerado pelo Kubb.
 */
export type EnrollmentFinancialDiscount = CourseRegistrationManagementFinancialDiscountViewModel;

export interface EnrollmentFinancialSummary {
  readonly totalValue: number;
  readonly paidValue: number;
  readonly remainingValue: number;
  readonly nextDueDate: string | null;
  readonly nextDueValue: number | null;
  readonly totalInstallments: number;
  readonly paidInstallments: number;
  readonly remainingInstallments: number;
  readonly hasEnrollmentFee: boolean;
  // Plan details
  readonly registrationTaxExpirationDate: string | null;
  readonly poloCanChangeRegistrationTax: boolean;
  readonly firstTuitionExpirationDate: string | null;
  readonly poloCanChangeFirstTuition: boolean;
  readonly isPoloAllowedToChangeFinancial: boolean;
  readonly descriptionPaymentMethod: string | null;
  readonly paymentInCashOrInstallments: string | null;
  readonly isScholarshipHolder: boolean;
  readonly planSummary: string | null;
  readonly discounts: readonly EnrollmentFinancialDiscount[];
}

// ─── Modal Data (subset used by Cancel/ChangePolo modals) ────────────────────

export interface EnrollmentModalData {
  readonly id: number;
  readonly personName: string;
  readonly personCpf: string;
  readonly personEmail: string;
  readonly status: EnrollmentStatus;
  readonly courseName: string;
  readonly className: string;
  readonly poleName: string | null;
  readonly enrollmentDate: string;
}

// ─── Status Change ───────────────────────────────────────────────────────────

export interface StatusChangeRequest {
  readonly enrollmentId: number;
  readonly newStatus: EnrollmentStatus;
  readonly reasonId: string;
  readonly subReasonId: string;
  readonly observation: string;
  readonly sendNotification: boolean;
  readonly sendCertificateByEmail: boolean;
}

// ─── Batch Status Change ─────────────────────────────────────────────────────

export interface BatchStatusChangeRequest {
  readonly enrollmentIds: readonly number[];
  readonly classStudentId: string;
  readonly newStatus: EnrollmentStatus;
  readonly reasonId: string;
  readonly subReasonId: string;
  readonly observation: string;
  readonly sendNotification: boolean;
  readonly sendCertificateByEmail: boolean;
  readonly scholarshipFilter: string;
}

// ─── Management Form Data ────────────────────────────────────────────────────

export interface EnrollmentManagementData {
  // Details
  readonly observation: string;
  readonly statusReason: string;
  readonly foreignStatus: string;
  // Course Access
  readonly isReleased: boolean;
  readonly releasedUntil: string;
  readonly releasedDays: number;
  // Documentation
  readonly documentationStatus: DocumentationStatus;
}

// ─── Status Reasons (for dropdowns) ──────────────────────────────────────────

export interface StatusReason {
  readonly id: string;
  readonly name: string;
  readonly subReasons: readonly { readonly id: string; readonly name: string }[];
}

// ─── Courses (for filter) ────────────────────────────────────────────────────

export interface CourseOption {
  readonly id: string;
  readonly name: string;
}

// ─── Polos (for filter) ──────────────────────────────────────────────────────

export interface PoloOption {
  readonly id: string;
  readonly name: string;
}
